<template>
    <v-dialog v-model="dialog" width="800" :fullscreen="isMobileOrTablet()" persistent>
        <v-card class="rounded-lg" style="display: flex; flex-direction: column; height: 100%;">
            <v-card-title style="background-color: #1a5b0f !important; display: flex; justify-content: space-between; align-items: center;">
                <div class="w-75 text-truncate text-white">
                    <span class="font-weight-bold text-h6">{{ dialog_title }}</span>
                    <div class="text-caption">{{ dialog_sub_title }}</div>
                </div>
                <v-btn v-if="loading == false" flat icon color="darkGreen" @click="closeDialog()">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="overflow-y: auto; max-height: calc(100vh - 100px);">
                <v-form ref="form" class="mt-2">
                    <v-row dense>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="compliance"
                                label="Compliance"
                                :rules="[formRules.required]"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                :items="complianceData"
                                item-title="compliance_name"
                                item-value="id"
                                return-object
                                >
                            </v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="set_id"
                                label="Set"
                                :rules="[formRules.required]"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                :items="courseSetData"
                                item-title="set_code"
                                item-value="id"
                                />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-date-input
                                v-model="deadline"
                                prepend-icon=""
                                prepend-inner-icon="mdi-calendar"
                                :rules="[formRules.required]"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                label="Deadline"
                            ></v-date-input>
                        </v-col>
                        <v-col v-if="compliance?.is_term_separated" cols="12" md="6">
                            <v-autocomplete
                                v-model="term_id"
                                label="Term"
                                :rules="[formRules.required]"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                :items="termData"
                                item-title="name"
                                item-value="id"
                                >
                            </v-autocomplete>
                        </v-col>
                        <v-divider></v-divider>
                        <v-col cols="12">
                            <v-switch
                                v-model="is_extended"
                                label="Is Extend Deadline"
                                color="primary"
                                inset
                            ></v-switch>
                        </v-col>
                        <v-col v-if="is_extended == true" cols="12" md="6">
                            <v-date-input
                                v-model="deadline_extension"
                                :rules="[formRules.required, newFormRules.extendDeadline]"
                                prepend-icon=""
                                prepend-inner-icon="mdi-calendar"
                                variant="outlined"
                                rounded="lg"
                                type="text"
                                color="darkGreen"
                                label="Deadline Extension"
                            ></v-date-input>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-3" style="margin-top: auto;">
                <v-spacer></v-spacer>
                <v-btn class="mr-1" :disabled="loading" text="Cancel" variant="outlined" elevation="1" @click="closeDialog()"></v-btn>
                <v-btn class="mr-1" :loading="loading" text="Save" variant="elevated" elevation="1" color="darkGreen" @click="validateForm()"></v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { defineComponent } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'

export default defineComponent({
     emits: ["refresh"],
     data(){
        return {
            dialog: false,
            dialog_title: null,
            dialog_sub_title: null,
            action: null,
            loading: false,

            id: null,
            set_id: null,
            deadline: null,
            compliance: null,
            compliance_id: null,
            term_id: null,
            is_extended: false,
            deadline_extension: null,

            complianceData: [],
            termData: [],
            courseSetData: [],

            newFormRules: {
                extendDeadline: (v) => {
                    if (!this.is_extended) return true;
                    if (!v) return 'Deadline Extension is required';
                    if (new Date(v) <= new Date(this.deadline)) {
                    return 'Extended deadline must be greater than the original deadline';
                    }
                    return true;
                }
            },
        }
     },
     methods: {
        initAdd(){
            this.dialog = true
            this.dialog_title = 'Schedules, Deadline and Extension'
            this.dialog_sub_title = 'Add new deadline/extention for compliances'
            this.action = 'add'

            this.initOptions()
        },
        initUpdate(data){
            this.dialog = true
            this.dialog_title = 'Schedules, Deadline and Extension'
            this.dialog_sub_title = 'Update deadline/extention for compliances'
            this.action = 'edit'

            this.initOptions()

            this.id = data.id
            this.set_id = data.set_id
            this.deadline = new Date(data.deadline)
            this.compliance = data.compliance
            this.compliance_id = data.compliance_id
            this.term_id = data.term_id
            this.is_extended = data.is_extended
            this.deadline_extension = data.deadline_extension ? new Date(data.deadline_extension) : null
        },
        async initOptions(){
            const res = await this.axiosCall(`/compliances`, 'get', true);
            if(res.data){
                this.complianceData = res.data
            }

            const courseSetRes = await this.axiosCall(`/course-set`, 'get', true);
            if(courseSetRes.data){
                this.courseSetData = courseSetRes.data
            }
        },
        async validateForm(){
            const { showSnackbar } = useSnackbar()
            const { valid } = await this.$refs.form.validate()

            if(valid){
                this.loading = true
                let formData = {
                    id: this.id,
                    set_id: this.set_id,
                    deadline: this.deadline,
                    compliance_id: this.compliance.id,
                    term_id: this.term_id,
                    is_extended: this.is_extended,
                    deadline_extension: this.deadline_extension,
                    semester_schoolyear_id: this.$store.state.semester_schoolyear.id
                }

                const token = localStorage.getItem('aums-token')
                if(this.action == 'add'){

                    const res = await this.axiosCall('/sets-deadline', 'post', formData);
                    if(res.data && res.data.status == 201){
                        this.loading = false
                        this.$emit('refresh')
                        showSnackbar('Set Deadline successfully added!')
                        this.$refs.form.reset()
                        this.closeDialog()
                    }
                    else if (res.data && res.data.status == 409){
                        this.loading = false
                        showSnackbar('A set deadline already exists for this compliance')
                    }
                }
                else if(this.action == 'edit'){
                    const res = await this.axiosCall(`/sets-deadline/${this.id}`, 'patch', formData);
                    if(res.data && res.data.status == 200){
                        this.loading = false
                        this.$emit('refresh')
                        showSnackbar('Set Deadline successfully updated!')
                        this.$refs.form.reset()
                        this.closeDialog()
                    }
                    else if (res.data && res.data.status == 409){
                        this.loading = false
                        showSnackbar('A set deadline already exists for this compliance')
                    }
                }
            }
        },
        closeDialog(){
            this.$refs.form.reset()
            this.deadline = null
            this.deadline_extension = null
            this.term_id = null,
            this.is_extended = false
            this.dialog = false
        }
     }
})
</script>

<style scoped>

</style>